import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

import { captureException } from "../lib/sentry";
import { showError } from "./ErrorToast";
import { i18n } from "../i18n";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Global error boundary — catches any unhandled render-phase exception and
 * shows a graceful recovery screen instead of a blank white page.
 *
 * The recovery link is deliberately a plain anchor because this boundary
 * wraps BrowserRouter itself. A router-dependent link would throw a second
 * error while rendering the fallback and turn a useful recovery screen into
 * a blank page.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error ? error.message : String(error ?? "unknown error");
    return { hasError: true, message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // PII-safe extra (MEMORY §6): only the component stack, no chart
    // payloads, names, or chart_ids.
    captureException(error, { componentStack: info.componentStack });
    // Keep the console.error so dev still sees the error in the terminal.
    console.error("[ErrorBoundary]", error, info.componentStack);
    // Surface the render failure to the user via the same toast that
    // catches unhandled rejections. Note: ErrorBoundary already renders a
    // full-page "The watch stopped." screen, but the toast helps the user
    // notice on the route they came from if they're already mid-flow.
    showError(i18n.t("ui.web.long_tail.errors.watch_stopped_toast"));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-walnut-900 px-6"
          role="alert"
        >
          <div className="max-w-md text-center">
            <p className="font-display italic text-4xl text-brass-300 mb-4">
              {i18n.t("ui.web.long_tail.errors.watch_stopped")}
            </p>
            <p className="font-body text-parchment-200 text-sm mb-3 leading-relaxed">
              {i18n.t("ui.web.long_tail.errors.unexpected_reading")}
            </p>
            <pre className="bg-walnut-800 border border-vermillion-700/40 text-vermillion-300 font-mono text-[11px] p-3 overflow-auto max-h-40 text-start mb-6">
              {this.state.message}
            </pre>
            <button
              onClick={() => this.setState({ hasError: false, message: "" })}
              className="font-caps text-[11px] tracking-[0.25em] uppercase text-brass-300 border border-brass-500/40 px-5 py-2 hover:bg-brass-500/10 transition-colors me-3"
            >
              {i18n.t("ui.web.long_tail.errors.try_again")}
            </button>
            <a
              href="/"
              className="font-caps text-[11px] tracking-[0.25em] uppercase text-parchment-400 border border-brass-500/20 px-5 py-2 hover:bg-brass-500/10 transition-colors inline-block"
            >
              {i18n.t("ui.web.long_tail.errors.back_home")}
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
